/** Pure helpers for the lenders screen. No services, so components may use them. */

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

/** Keystone's key shape: lowercase, starts with a letter, letters, digits and `_`. */
export function isValidKey(value: string): boolean {
  return KEY_PATTERN.test(value);
}

/** A key derived from a human name, so nobody has to invent one. */
export function keyFromName(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) {
    return '';
  }
  return /^[a-z]/.test(slug) ? slug : `n_${slug}`;
}

/** `Terms of Business.pdf` reads as `Terms of Business`. */
export function titleFromFilename(filename: string): string {
  const dot = filename.lastIndexOf('.');
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  return stem.trim();
}

export function extensionOf(filename: string | null | undefined): string {
  if (!filename) {
    return '';
  }
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot) : '';
}

/** The countries a lender is offered in, first the ones LendWell serves today. */
export const COUNTRIES: readonly (readonly [string, string])[] = [
  ['IE', 'Ireland'],
  ['GB', 'United Kingdom'],
  ['BE', 'Belgium'],
  ['FR', 'France'],
  ['DE', 'Germany'],
  ['IT', 'Italy'],
  ['NL', 'Netherlands'],
  ['PT', 'Portugal'],
  ['ES', 'Spain'],
  ['US', 'United States'],
];

export function countryName(code: string | null | undefined): string {
  if (!code) {
    return 'No country';
  }
  return COUNTRIES.find(([value]) => value === code)?.[1] ?? code;
}

/** A regional-indicator flag for an alpha-2 code. */
export function countryFlag(code: string | null | undefined): string {
  if (!code || !/^[A-Z]{2}$/.test(code)) {
    return '';
  }
  return String.fromCodePoint(...[...code].map((letter) => 0x1f1a5 + letter.charCodeAt(0)));
}

export function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').split(/\s+/).filter((word) => /[A-Za-z0-9]/.test(word));
  if (words.length === 0) {
    return '?';
  }
  const letters = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return letters.toUpperCase();
}

export interface CountryGroup<T> {
  country: string | null;
  lenders: T[];
}

/** Lenders grouped by country, Ireland first and the rest by name. */
export function groupByCountry<T extends { country: string | null; name: string | null }>(
  lenders: readonly T[],
): CountryGroup<T>[] {
  const groups = new Map<string | null, T[]>();
  for (const lender of lenders) {
    const rows = groups.get(lender.country) ?? [];
    rows.push(lender);
    groups.set(lender.country, rows);
  }
  const order = (country: string | null) => {
    const index = COUNTRIES.findIndex(([value]) => value === country);
    return index === -1 ? COUNTRIES.length : index;
  };
  return [...groups.entries()]
    .sort(([a], [b]) => order(a) - order(b) || countryName(a).localeCompare(countryName(b)))
    .map(([country, rows]) => ({
      country,
      lenders: [...rows].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    }));
}

/** Keystone's own order: `(position, createdAt, id)`, since `position` is not unique. */
export function sortDocuments<
  T extends { id: string; position: number | null; createdAt: string | null },
>(documents: readonly T[]): T[] {
  return [...documents].sort(
    (a, b) =>
      (a.position ?? 0) - (b.position ?? 0) ||
      (a.createdAt ?? '').localeCompare(b.createdAt ?? '') ||
      a.id.localeCompare(b.id),
  );
}

/** The whole list's ids with one moved a step, which is what a reorder must send. */
export function moveId(ids: readonly string[], id: string, step: -1 | 1): string[] | null {
  const from = ids.indexOf(id);
  const to = from + step;
  if (from === -1 || to < 0 || to >= ids.length) {
    return null;
  }
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
